import { useMemo, useState } from "react";
import { Check, ChevronDown, Layers, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const HOA_MODULES = {
  requests: "Requests", roster: "Residents", rules: "Rules", contacts: "Trusted Pros", profile: "Community Profile",
  field_services: "Field Services", reservations: "Reservations", events: "Events", renewals: "Renewals",
  emergency: "Emergency Alerts", broadcasts: "Messages", team: "Management team", portal_requests: "My Requests",
  public_page: "Public Page", surveys: "Surveys", signatures: "E-Sign", reports: "Reports", documents: "AI Documents",
  conversations: "Conversations", feedback: "Feedback",
};
const HOA_ROLES = { hoa_manager: "Community Manager", board_member: "Board Member", regional_manager: "Community Manager", company_admin: "Administrator" };
const TERM_ORDER = ["person", "org", "org_short", "unit", "household", "board", "board_group", "staff", "team", "request", "portal", "dues", "rules", "office", "mgmt_co"];

// "HOA word → pack word" for everything a pack renames, grouped: words, menu names, roles.
export function renamedRows(pack, hoa) {
  if (!pack || pack.is_default) return { word: [], menu: [], role: [] };
  const hv = hoa?.vocab_preview?.terms || {};
  const pv = pack.vocab_preview?.terms || {};
  const word = [];
  for (const k of TERM_ORDER) {
    const a = hv[k], b = pv[k];
    if (!a || !b || (a.one === b.one && a.many === b.many)) continue;
    word.push({ from: a.one === a.many ? a.one : `${a.one} · ${a.many}`, to: b.one === b.many ? b.one : `${b.one} · ${b.many}` });
  }
  const pm = pack.vocab_preview?.modules || {};
  const menu = Object.entries(HOA_MODULES).filter(([k, from]) => pm[k] && pm[k] !== from).map(([k, from]) => ({ from, to: pm[k] }));
  const pr = pack.vocab_preview?.roles || {};
  const role = Object.entries(HOA_ROLES).filter(([k, from]) => pr[k] && pr[k] !== from).map(([k, from]) => ({ from, to: pr[k] }));
  return { word, menu, role };
}

const GROUP_LABEL = { word: "Words", menu: "Menu names", role: "Roles" };

// The rename table, readable on a phone: one row per rename, wraps instead of truncating.
export function RenameTable({ pack, hoa, testid = "industry" }) {
  const groups = useMemo(() => renamedRows(pack, hoa), [pack, hoa]);
  const off = Object.entries(pack?.features || {}).filter(([, on]) => on === false).map(([k]) => HOA_MODULES[k] || k.replace(/_/g, " "));
  return (
    <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 sm:p-4" data-testid={`${testid}-renames`}>
      {["word", "menu", "role"].map((g) => groups[g].length > 0 && (
        <div key={g} className="mb-3 last:mb-0">
          <div className="text-[10px] font-bold uppercase tracking-[.12em] text-ink/50 mb-1">{GROUP_LABEL[g]}</div>
          <div className="divide-y divide-sand-200/70">
            {groups[g].map((r, i) => (
              <div key={i} data-testid={`${testid}-rename-row`} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-2 py-1.5 text-[13px] items-start">
                <span className="text-ink/55 line-through decoration-ink/30 break-words">{r.from}</span>
                <span className="text-ink/40">→</span>
                <span className="font-semibold text-ink break-words">{r.to}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {off.length > 0 && <p className="text-xs text-ink/60 mt-2" data-testid={`${testid}-off-note`}>Starts with <b>{off.join(", ")}</b> switched off — turn them back on any time under Capabilities.</p>}
      <p className="text-xs text-ink/50 mt-1">Nothing is hidden: every module stays, it just speaks this industry's language.</p>
    </div>
  );
}

// One row (current pack + chevron) → searchable list, one line per pack. Scales to any number
// of packs. "See what changes" opens the rename table on demand.
export function IndustryPicker({ packs = [], value, onChange, disabled, testid = "industry", showChanges = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [changes, setChanges] = useState(false);
  const list = packs.length ? packs : [{ id: "hoa", name: "Homeowners association", tagline: "Today's wording — nothing changes.", is_default: true }];
  const hoa = list.find((p) => p.is_default) || null;
  const selected = list.find((p) => p.id === value) || null;
  const filtered = list.filter((p) => !q || `${p.name} ${p.tagline || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid={`${testid}-picker`}>
      <Popover open={open} onOpenChange={(o) => { if (!disabled) { setOpen(o); setQ(""); } }}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} data-testid={`${testid}-picker-trigger`} aria-haspopup="listbox" aria-expanded={open}
            className="w-full flex items-center gap-3 rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-left hover:border-ink/40 disabled:cursor-default disabled:hover:border-sand-200">
            <span className="h-8 w-8 rounded-lg bg-redline-50 flex items-center justify-center shrink-0"><Layers className="h-4 w-4 text-redline-500" strokeWidth={1.75} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-ink truncate" data-testid={`${testid}-picker-value`}>{selected?.name || "Choose an industry"}</span>
              {selected?.tagline && <span className="block text-xs text-ink/55 truncate">{selected.tagline}</span>}
            </span>
            {selected?.is_default && <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-sand-100 text-ink/60">Today's wording</span>}
            {!disabled && <ChevronDown className={`h-4 w-4 text-ink/50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-1.5 rounded-xl" data-testid={`${testid}-picker-list`}>
          {list.length > 5 && (
            <div className="flex items-center gap-2 px-2.5 py-2 border-b border-sand-100 mb-1">
              <Search className="h-3.5 w-3.5 text-ink/40" />
              <input autoFocus data-testid={`${testid}-picker-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search industries…" className="flex-1 bg-transparent text-sm outline-none" />
            </div>
          )}
          <div role="listbox" className="max-h-72 overflow-y-auto">
            {filtered.map((p) => {
              const on = p.id === value;
              return (
                <button key={p.id} type="button" role="option" aria-selected={on} data-testid={`${testid}-pack-${p.id}`}
                  onClick={() => { onChange?.(p.id); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-sand-50 ${on ? "bg-sand-50" : ""}`}>
                  <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${on ? "bg-ink border-ink text-white" : "border-sand-300"}`}>{on && <Check className="h-3 w-3" strokeWidth={3} />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm font-semibold truncate">{p.name}</span>
                      {p.is_default && <span className="text-[10px] font-semibold uppercase tracking-wide text-ink/50">default</span>}
                      {typeof p.communities === "number" && p.communities > 0 && <span className="text-[10px] text-ink/50">{p.communities} live</span>}
                    </span>
                    {p.tagline && <span className="block text-xs text-ink/55 truncate">{p.tagline}</span>}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && <div className="px-2.5 py-3 text-sm text-ink/50">No industry matches.</div>}
          </div>
        </PopoverContent>
      </Popover>

      {showChanges && selected && !selected.is_default && (
        <div className="mt-2">
          <button type="button" data-testid={`${testid}-see-changes`} onClick={() => setChanges((c) => !c)} className="text-xs font-semibold text-redline-600 hover:text-redline-700 inline-flex items-center gap-1">
            {changes ? "Hide what changes" : "See what changes"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${changes ? "rotate-180" : ""}`} />
          </button>
          {changes && <div className="mt-2"><RenameTable pack={selected} hoa={hoa} testid={testid} /></div>}
        </div>
      )}
      {showChanges && selected?.is_default && <p className="text-xs text-muted-foreground mt-2" data-testid={`${testid}-hoa-note`}>Today's vocabulary — nothing is renamed.</p>}
    </div>
  );
}
